import {
  createModule,
  action,
  mutation,
} from 'vuex-class-component';
import { RepositoryFactory } from '@/repository/factory/repositoryFactory';
import {
  FindOneAccountQuery,
  AccountDetailResponse,
  AccountBasicResponse,
  AccountTotalFootprintsResponse,
  AccountAllParticipationProjectsResponse,
  AccountParticipationOrganizationsResponse,
  AccountFootprintsResponse,
  FindAccountFootprintsQuery,
  AccountBasicParticipationProjectsResponse,
} from '@/@types/Account';

const VuexModule = createModule({
  namespaced: 'theoryApiAccountsStore',
  strict: false,
});

export class AccountsStore extends VuexModule {
  private readonly accountsRepository = RepositoryFactory.theoryApi.accountsRepository();
  /** state */
  private accountDetail: AccountDetailResponse | null = null;
  private accountBasic: AccountBasicResponse | null = null;
  private accountTotalFootprints: AccountTotalFootprintsResponse | null = null;
  private accountParticipationProjects: AccountAllParticipationProjectsResponse  | null = null;
  private accountBasicParticipationProjects: AccountBasicParticipationProjectsResponse | null = null;
  private accountParticipationOrganizations: AccountParticipationOrganizationsResponse  | null = null;
  private accountFootprints: AccountFootprintsResponse | null = null;

  @mutation
  private setAccountDetail(payload: AccountDetailResponse) {
    this.accountDetail = payload;
  }

  @mutation
  private setAccountBasic(payload: AccountBasicResponse) {
    this.accountBasic = payload;
  }

  @mutation
  private setAccountTotalFootprints(payload: AccountTotalFootprintsResponse) {
    this.accountTotalFootprints = payload;
  }

  @mutation
  private setAccountParticipationProjects(
    payload: AccountAllParticipationProjectsResponse,
  ) {
    this.accountParticipationProjects = payload;
  }

  @mutation
  private setAccountBasicParticipationProjects(
    payload: AccountBasicParticipationProjectsResponse,
  ) {
    this.accountBasicParticipationProjects = payload;
  }

  @mutation
  private setAccountParticipationOrganizations(
    payload: AccountParticipationOrganizationsResponse,
  ) {
    this.accountParticipationOrganizations = payload;
  }

  @mutation
  private setAccountFootprints(payload: AccountFootprintsResponse) {
    if (!this.accountFootprints) {
      this.accountFootprints = payload;
      return;
    }
    this.accountFootprints.results = [...this.accountFootprints.results, ...payload.results];
    this.accountFootprints.pagination = payload.pagination;
  }

  /** アカウント詳細取得 */
  @action
  async fetchAccountDetail(accountId: number) {
    const results = await this.accountsRepository.findOneAccountDetail(accountId);
    this.setAccountDetail(results);
  }

  /** アカウント詳細取得(detail-flag=false) */
  @action
  async fetchAccountBasic(params: {
    accountId: number,
    query: FindOneAccountQuery,
  }) {
    const results = await this.accountsRepository.findOneAccountBasic(
      params.accountId,
      params.query
    );
    this.setAccountBasic(results);
  }

  /** アカウント足跡件数取得 */
  @action
  async fetchAccountTotalFootprints(accountId: number) {
    const results = await this.accountsRepository.getAccountTotalFootprints(accountId);
    this.setAccountTotalFootprints(results);
  }

  /** アカウント参画プロジェクト一覧取得 allモード */
  @action
  async fetchAccountParticipationProjects(accountId: number) {
    const results = await this.accountsRepository.findAllAccountParticipationProjects(
      accountId,
    );
    this.setAccountParticipationProjects(results);
  }

  /** アカウント参画プロジェクト一覧取得 basicモード */
  @action
  async fetchAccountBasicParticipationProjects(params: {
    accountId: number,
    organizationId: number,
  }) {
    const results = await this.accountsRepository.findBasicAccountParticipationProjects(
      params.accountId,
      params.organizationId,
    );
    this.setAccountBasicParticipationProjects(results);
  }

  /** アカウント参画組織一覧取得 */
  @action
  async fetchAccountParticipationOrganizations(accountId: number) {
    const results = await this.accountsRepository.findAllAccountParticipationOrganizations(accountId);
    this.setAccountParticipationOrganizations(results);
  }

  /** アカウント足跡一覧取得 */
  @action
  async fetchAccountFootprints(params: {
    accountId: number,
    query: FindAccountFootprintsQuery,
  }) {
    const results = await this.accountsRepository.findAccountFootprints(
      params.accountId,
      params.query
    );
    this.setAccountFootprints(results);
  }

  get getAccountDetail() {
    return this.accountDetail;
  }

  get getAccountBasic() {
    return this.accountBasic;
  }

  get getAccountTotalFootprints() {
    return this.accountTotalFootprints;
  }

  get getAccountParticipationProjects() {
    return this.accountParticipationProjects;
  }

  get getAccountBasicParticipationProjects() {
    return this.accountBasicParticipationProjects;
  }

  get getAccountParticipationOrganizations() {
    return this.accountParticipationOrganizations;
  }

  get getAccountFootprints() {
    return this.accountFootprints;
  }
}
